"use client";

import Image from "next/image";
import Link from "next/link";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperType } from "swiper";
import { Autoplay, Navigation } from "swiper/modules";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import "swiper/css";
import "swiper/css/navigation";

export default function TeamSection() {
  type TeamMember = {
    id: number;
    name: string;
    designation: string;
    description: string;
    image: string;
  };

  const swiperRef = useRef<SwiperType | null>(null);
  const fetchedRef = useRef(false); // ✅ prevent double fetch
  const [team, setTeam] = useState<TeamMember[]>([]);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    const fetchTeams = async () => {
      try {
        const res = await fetch("https://portal.renvixx.com/api/teams");
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        const contentType = res.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
          throw new Error("Received non-JSON response from server");
        }
        const json = (await res.json()) as { data: TeamMember[] };

        // ✅ Sort so ID 1 comes first
        const sortedData = json.data.sort((a, b) => a.id - b.id);

        setTeam(sortedData);
      } catch (error) {
        console.error("Failed to load team data", error);
      }
    };

    fetchTeams();
  }, []);

  return (
    <section className="py-16 max-w-7xl mx-auto px-6 lg:px-8 overflow-hidden">
      {/* Header */}
      <div className="text-center mb-10">
        <p className="text-sm text-gray-500 uppercase tracking-widest mb-2">
          Meet Our Team
        </p>
        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
          Meet the Faces
        </h2>
        <h3 className="text-3xl sm:text-4xl font-bold text-[#0B2149]">
          Behind the Structure
        </h3>
      </div>

      {/* Swiper Wrapper */}
      <div
        className="relative max-w-7xl mx-auto px-4"
        onMouseEnter={() => swiperRef.current?.autoplay?.stop()}
        onMouseLeave={() => swiperRef.current?.autoplay?.start()}
      >
        {/* Navigation */}
        <div className="absolute -left-6 top-1/2 z-20 -translate-y-1/2 hidden md:block">
          <div className="swiper-button-prev-custom bg-[#2a6380] text-white p-3 rounded-full cursor-pointer shadow-md hover:bg-[#33c1c7] transition">
            <ChevronLeft size={22} />
          </div>
        </div>
        <div className="absolute -right-6 top-1/2 z-20 -translate-y-1/2 hidden md:block">
          <div className="swiper-button-next-custom bg-[#2a6380] text-white p-3 rounded-full cursor-pointer shadow-md hover:bg-[#33c1c7] transition">
            <ChevronRight size={22} />
          </div>
        </div>

        {/* Swiper */}
        <Swiper
          modules={[Autoplay, Navigation]}
          spaceBetween={30}
          slidesPerView={1}
          loop={team.length > 3} // ✅ avoids loop duplication
          navigation={{
            nextEl: ".swiper-button-next-custom",
            prevEl: ".swiper-button-prev-custom",
          }}
          autoplay={{
            delay: 3000,
            disableOnInteraction: false,
          }}
          breakpoints={{
            640: { slidesPerView: 1 },
            768: { slidesPerView: 2 },
            1024: { slidesPerView: 3 },
          }}
          onSwiper={(swiper) => (swiperRef.current = swiper)}
          className="team-swiper"
        >
          {team.map((member) => (
            <SwiperSlide key={member.id} className="flex">
              <div className="flex flex-col bg-white rounded-lg shadow-md border overflow-hidden border-[#838383] hover:shadow-lg transition-all duration-300 w-full">
                {/* Image */}
                <div className="relative w-full h-80 sm:h-96 group overflow-hidden">
                  <div className="relative w-full h-full flex justify-center items-end">
                    <Image
                      src={member.image}
                      alt={member.name}
                      fill
                      className="object-cover object-top z-10 transition-transform duration-500 ease-in-out group-hover:scale-110"
                      sizes="100vw"
                    />
                  </div>
                </div>

                {/* Text */}
                <div className="p-6 text-center flex flex-col justify-between flex-grow">
                  <div>
                    <h4 className="text-lg font-semibold text-[#2a6380] mb-1">
                      {member.name}
                    </h4>
                    <p className="text-sm font-medium text-[#000000] mb-2">
                      {member.designation}
                    </p>
                    <p className="text-[#000000] text-sm leading-relaxed overflow-hidden whitespace-nowrap">
                      <span className="inline-block animate-marquee">
                        {member.description}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      {/* Button */}
      <div className="flex justify-center mt-10">
        <Link
          href="/team"
          className="relative overflow-hidden inline-block border-2 border-[#2a6380] bg-[#2a6380] text-white font-semibold px-6 py-3 rounded transition-all duration-500 ease-in-out group"
        >
          <span className="relative z-10 transition-colors duration-500 group-hover:text-[#2a6380]">
            Meet Our Team
          </span>
          <span className="absolute inset-0 bg-white scale-[-1] -translate-x-full group-hover:translate-x-0 transition-transform duration-500 ease-in-out"></span>
        </Link>
      </div>
    </section>
  );
}
